import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Input, Row, Col, Typography,img } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import authService from '../services/authService';
import loginBg from '../assets/images/login-bg.jpg';
import mlLogo from '../assets/images/ML-logo.svg';
import 'antd/dist/reset.css';

const { Title, Text } = Typography;

const loginStyles = `
  html, body {
    overflow: hidden;
    height: 100%;
  }

  .login-container {
    padding-top: 20px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    min-height: 100vh;
    overflow: hidden;
  }

  .login-logo-wrapper {
    margin-bottom: 20px;
  }

  .login-logo {
    padding-top: 10px;
    height: 50px;
    width: auto;
  }

  .login-row {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    elevation: 4;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-radius: 8px 8px 0 0 ;
  }

  .login-left-section {
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px 0 0 16px;
    position: relative;
    background-color:#fff;
    border-right: 1px solid #e9e9e9;
  }
  .left-img{
  width:80%;
  

  
  }
  .login-left-content {
    text-align: center;
    color: #fff;
    padding: 40px;
    position: relative;
    z-index: 1;
  }

  .login-icon {
    font-size: 80px;
    margin-bottom: 20px;
  }

  .login-title {
    color: #fff !important;
    margin-bottom: 16px !important;
  }

  .login-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
  }

  .login-features {
    margin-top: 40px;
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  .login-feature-item {
    text-align: center;
  }

  .login-feature-icon {
    font-size: 40px;
  }

  .login-feature-text {
    color: rgba(255, 255, 255, 0.9);
  }

  .login-right-section {
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    border-radius: 0 16px 16px 0;
  }

  .login-form-wrapper {
    width: 100%;
    max-width: 350px;
    max-height: 450px;
    overflow-y: auto;
  }

  .login-form-title {
    text-align: center;
    margin-bottom: 8px !important;
  }

  .login-form-subtitle {
    text-align: center;
    display: block;
    margin-bottom: 24px;
  }

  .login-error-alert {
    margin-bottom: 16px;
  }

  .login-submit-btn {
    background: #667eea !important;
    border-color: #667eea !important;
  }

  .login-submit-btn:hover {
    background: #5568d3 !important;
    border-color: #5568d3 !important;
  }

  .login-footer-link {
    text-align: center;
    margin-top: 16px;
  }

  .login-contact-link {
    cursor: pointer;
  }

  .login-branding-bar {
    width: 100%;
    max-width: 800px;
    margin-top: 0;
    background: #8a8a8a;
    color: #fff;
    padding: 12px 20px;
    text-align: center;
    border-radius: 0 0 8px 8px;
    font-size: 14px;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .login-left-section {
      display: none;
    }

    .login-right-section {
      border-radius: 8px;
    }

    .login-branding-bar {
      border-radius: 0 0 8px 8px;
    }
  }
`;

const LOGIN_ERROR_KEY = 'login_error_message';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Check for error from session storage on component mount
  useEffect(() => {
    const storedError = sessionStorage.getItem(LOGIN_ERROR_KEY);
    if (storedError) {
      setError(storedError);
      sessionStorage.removeItem(LOGIN_ERROR_KEY);
      // Auto-clear error after 5 seconds
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      const result = await authService.login(email, password);

      if (result.success) {
        if (typeof onLoginSuccess === 'function') {
          try { onLoginSuccess(); } catch (_) {}
        }

        try {
          let companyId = null;
          try {
            const me = await authService.getUserDetails();
            companyId = me?.company_id || me?.company?.id || null;
          } catch (_) {
            const stored = authService.getUser ? authService.getUser() : null;
            companyId = stored?.company_id || stored?.company?.id || stored?.companyId || null;
          }

          if (companyId) {
            navigate(`/`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
          return;
        } catch (err) {
          console.error('Redirect failed', err);
          navigate('/', { replace: true });
          return;
        }
      } else {
        const errorMsg = result.message || 'Login failed';
        setError(errorMsg);
        // Store error in session storage to persist across page reloads
        sessionStorage.setItem(LOGIN_ERROR_KEY, errorMsg);
      }
    } catch (err) {
      console.error(err);
      const errorMsg = err?.message || 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
      // Store error in session storage to persist across page reloads
      sessionStorage.setItem(LOGIN_ERROR_KEY, errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{loginStyles}</style>
      <div className="login-container">
        {/* LOGO */}
        <div className="login-logo-wrapper">
          <img src={mlLogo} alt="Metroguild Logo" className="login-logo" />
        </div>

        <Row className="login-row" gutter={0}>
          {/* LEFT SECTION - Illustration with Background Image */}
          <Col xs={0} sm={0} md={12} lg={12} xl={12} className="login-left-section">
           <img src={loginBg} alt='' className='left-img' >
           </img>
          </Col>

          {/* RIGHT SECTION - Login Form */}
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="login-right-section">
            <div className="login-form-wrapper">
              <Title level={3} className="login-form-title">Welcome Back</Title>
              <Text className="login-form-subtitle">
                Sign in to your account to continue
              </Text>

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => {
                    setError('');
                    sessionStorage.removeItem(LOGIN_ERROR_KEY);
                  }}
                  className="login-error-alert"
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark="optional"
              >
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Invalid email format' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: 'Please enter your password' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    size="large"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    className="login-submit-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-footer-link">
                <Text type="secondary">
                  Don't have an account? <Text type="primary" className="login-contact-link">Contact admin</Text>
                </Text>
              </div>
            </div>
          </Col>
        </Row>

        {/* BRANDING BAR */}
        <div className="login-branding-bar">
          © Metroguild Inc
        </div>
      </div>
    </>
  );
};

export default Login;
